import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { IntegratedErgonomicToolDTO } from '@/components/ui/Inputs/types/response/customReport';
import { ErgonomicToolMapper, ErgonomicToolsName } from '@/components/ui/Inputs/types/response/ergonomicTool';
import { useSocketReport } from '@/hooks/useSocket';
import { QUERY_KEYS } from '@/utils/query-keys';

import { KimMHOReportService } from '@/infra/services/kim-mho-report';
import { KimPushPullReportService } from '@/infra/services/kim-pp-report';
import { NioshReportService } from '@/infra/services/niosh-report';
import { StrainIndexReportService } from '@/infra/services/strain-index-report';

interface UseErgonomicToolReportsParams {
	integratedTools: IntegratedErgonomicToolDTO[];
	fileId: string;
	organizationId: string;
	companyId: string;
}

const nioshService = NioshReportService.getInstance();
const kimMhoService = KimMHOReportService.getInstance();
const kimPpService = KimPushPullReportService.getInstance();
const strainIndexService = StrainIndexReportService.getInstance();

type ToolConfig = {
	queryKey: string;
	queryFn: (params: { organization_id: string; company_id: string; file_id: string }) => Promise<any>;
};

const TOOL_CONFIG: Record<ErgonomicToolsName, ToolConfig> = {
	niosh: {
		queryKey: QUERY_KEYS.GET_NIOSH_REPORT,
		queryFn: (params) => nioshService.getNioshReport(params)
	},
	kim_mho: {
		queryKey: QUERY_KEYS.GET_KIM_MHO_REPORT,
		queryFn: (params) => kimMhoService.getReportByFileId(params)
	},
	kim_pp: {
		queryKey: QUERY_KEYS.GET_KIM_PP_REPORT,
		queryFn: (params) => kimPpService.getReport(params)
	},
	strain_index: {
		queryKey: QUERY_KEYS.GET_STRAIN_INDEX_REPORT,
		queryFn: (params) => strainIndexService.getReport(params)
	}
};

const SOCKET_TO_QUERY_KEY: Record<string, string> = {
	niosh: QUERY_KEYS.GET_NIOSH_REPORT,
	kimMHO: QUERY_KEYS.GET_KIM_MHO_REPORT,
	kimPushPull: QUERY_KEYS.GET_KIM_PP_REPORT,
	strainIndex: QUERY_KEYS.GET_STRAIN_INDEX_REPORT
};

export function useErgonomicToolReports({
	integratedTools,
	fileId,
	organizationId,
	companyId
}: UseErgonomicToolReportsParams) {
	const queryClient = useQueryClient();
	const { niosh, kimMHO, kimPushPull, strainIndex } = useSocketReport();
	const enabled = !!organizationId && !!companyId && !!fileId;
	const params = { organization_id: organizationId, company_id: companyId, file_id: fileId };

	useEffect(() => {
		const socketEvents = { niosh, kimMHO, kimPushPull, strainIndex };
		for (const [key, event] of Object.entries(socketEvents)) {
			if (event?.data?.file_id === fileId && event?.status) {
				const queryKey = SOCKET_TO_QUERY_KEY[key];
				if (queryKey) {
					queryClient.invalidateQueries([queryKey]);
				}
			}
		}
	}, [niosh, kimMHO, kimPushPull, strainIndex, fileId, queryClient]);

	const queries = useQueries({
		queries: (integratedTools ?? []).map((tool) => {
			const config = TOOL_CONFIG[tool.name];
			return {
				queryKey: [config.queryKey, params],
				queryFn: () => config.queryFn(params),
				enabled: enabled && !!config,
				retry: 0
			};
		})
	});

	const isLoading = queries.some((q) => q.isLoading);

	const ergonomicToolMapper: ErgonomicToolMapper = useMemo(() => {
		const mapper = {} as ErgonomicToolMapper;

		(integratedTools ?? []).forEach((tool, index) => {
			const query = queries[index];
			const data = query?.data;
			const isValidReport = !!data && !query.isError && data.status !== 'failed' && !!data.id;
			mapper[tool.name] = {
				isActive: isValidReport,
				data: isValidReport ? data : undefined
			};
		});

		return mapper;
	}, [integratedTools, queries]);

	const disabled = useMemo(() => {
		if (!integratedTools?.length) return false;
		return !integratedTools.some((tool) => ergonomicToolMapper[tool.name]?.isActive);
	}, [integratedTools, ergonomicToolMapper]);

	return { ergonomicToolMapper, isLoading, disabled };
}
